package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.settings.phase
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.settings.ActorChoice
import co.maytes.standupbot.dashboard.features.settings.AuditExportCopy
import co.maytes.standupbot.dashboard.features.settings.AuditFilter
import co.maytes.standupbot.dashboard.features.settings.AuditLogColumns
import co.maytes.standupbot.dashboard.features.settings.AuditLogFilters
import co.maytes.standupbot.dashboard.features.settings.AuditLogRow
import co.maytes.standupbot.dashboard.features.settings.AuditLogView
import co.maytes.standupbot.dashboard.features.settings.LogSummary
import co.maytes.standupbot.dashboard.features.settings.LogTable
import co.maytes.standupbot.dashboard.features.settings.LogTableLoadingRows
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.SettingsEmpty
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.settings.rememberSettingsStore
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.csv.CsvWriter
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

private const val LOADING_ROWS = 6

@Composable
fun AuditLogSection() {
    val auth = rememberAuthStore()
    val store = rememberSettingsStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id

    var filter by remember { mutableStateOf(AuditFilter()) }
    var loadingMore by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }
    var exporting by remember { mutableStateOf(false) }
    val ui = rememberUiStore()

    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) {
            store.refreshAudit(workspaceId, filter)
            if (store.members.isEmpty()) store.refreshMembers(workspaceId)
        }
    }

    LaunchedEffect(filter, workspaceId) {
        if (!workspaceId.isNullOrBlank()) store.refreshAudit(workspaceId, filter)
    }

    val actors by remember {
        derivedStateOf {
            store.members.map { ActorChoice(it.userId, it.displayName) }
        }
    }

    val visibleEntries = AuditLogView.visibleEntries(store.auditEntries, searchQuery)
    val showSummary = store.auditState == LoadState.Ready ||
        (store.auditState == LoadState.Loading && store.auditEntries.isNotEmpty())

    SettingsPage(
        group = SettingsGroup.SecurityAndData,
        title = "Audit log",
        subtitle = if (showSummary) {
            { AuditSubtitle(AuditLogView.summary(visibleEntries.size, store.auditHasMore, filter, searchQuery)) }
        } else {
            null
        },
        action = {
            Button(
                label = if (exporting) AuditExportCopy.BUTTON_BUSY else AuditExportCopy.BUTTON,
                variant = ButtonVariant.Secondary,
                enabled = !exporting && !workspaceId.isNullOrBlank(),
                loading = exporting,
                onClick = {
                    val wid = workspaceId ?: return@Button
                    scope.launch {
                        exporting = true
                        when (val result = store.exportAuditLog(wid, filter)) {
                            is Result.Ok -> {
                                downloadCsv(result.value, AuditExportCopy.fileName(Clock.System.todayIn(TimeZone.UTC)))
                                ui.showToast(AuditExportCopy.DONE, ToastKind.Success)
                            }
                            is Result.Err -> ui.showToast(AuditExportCopy.failureMessage(result.error), ToastKind.Error)
                        }
                        exporting = false
                    }
                },
            )
        },
    ) {
        Column(modifier = Modifier.fillMaxWidth().classNames(Motion.ENTER_CLASS)) {
            AuditLogFilters(
                filter = filter,
                actors = actors,
                query = searchQuery,
                onQueryChange = { searchQuery = it },
                onChange = { filter = it },
            )
            SpanText(
                text = AuditLogView.filterHelp,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .margin(top = Spacing.xs, bottom = Spacing.md),
            )
            StateSwap(state = store.auditState.phase()) {
                when (store.auditState) {
                    LoadState.Idle, LoadState.Loading -> LogTable(AuditLogColumns, ariaLabel = "Audit log") {
                        LogTableLoadingRows(AuditLogColumns, LOADING_ROWS)
                    }
                    is LoadState.Error -> SettingsPanel {
                        SettingsError(what = AuditLogView.RESOURCE) {
                            val wid = workspaceId ?: return@SettingsError
                            scope.launch { store.refreshAudit(wid, filter) }
                        }
                    }
                    LoadState.Ready -> {
                        if (visibleEntries.isEmpty()) {
                            SettingsEmpty(
                                text = AuditLogView.emptyText(filter, searchQuery, store.auditHasMore),
                                action = if (AuditLogView.isFiltered(filter, searchQuery)) {
                                    {
                                        Button(
                                            label = AuditLogView.CLEAR_FILTERS,
                                            variant = ButtonVariant.Secondary,
                                            size = ButtonSize.Sm,
                                            onClick = {
                                                filter = AuditFilter()
                                                searchQuery = ""
                                            },
                                        )
                                    }
                                } else {
                                    null
                                },
                            )
                        } else {
                            LogTable(AuditLogColumns, ariaLabel = "Audit log") {
                                visibleEntries.forEach { entry -> key(entry.id) { AuditLogRow(entry = entry) } }
                            }
                        }
                        if (store.auditHasMore) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = Spacing.md)
                                    .styleModifier { property("justify-content", "center") },
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Button(
                                    label = if (loadingMore) AuditLogView.LOADING_MORE else AuditLogView.LOAD_MORE,
                                    variant = ButtonVariant.Secondary,
                                    size = ButtonSize.Sm,
                                    enabled = !loadingMore,
                                    onClick = {
                                        val wid = workspaceId ?: return@Button
                                        loadingMore = true
                                        scope.launch {
                                            store.loadMoreAudit(wid, filter)
                                            loadingMore = false
                                        }
                                    },
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AuditSubtitle(summary: LogSummary) {
    SettingsSubtitle {
        Strong(summary.strong)
        Plain(summary.plain)
    }
}

private fun downloadCsv(
    contents: String,
    fileName: String,
) {
    val blob = Blob(arrayOf(CsvWriter.withByteOrderMark(contents)), BlobPropertyBag(type = "text/csv;charset=utf-8"))
    val url = URL.createObjectURL(blob)
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url
    a.download = fileName
    document.body?.appendChild(a)
    a.click()
    document.body?.removeChild(a)
    URL.revokeObjectURL(url)
}
